import { useState, useEffect } from 'react';
import { getAdminInferenceLogDetail } from '../api';
import { AdminInferenceLogDetail } from '../types';

interface InspectModalProps {
  inferenceLogId: string;
  onClose: () => void;
}

// Copy to clipboard helper
function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(
    () => {
      // Success - could add a toast notification here if desired
      console.log('Copied to clipboard');
    },
    (err) => {
      console.error('Failed to copy to clipboard:', err);
    }
  );
}

export function InspectModal({ inferenceLogId, onClose }: InspectModalProps) {
  const [log, setLog] = useState<AdminInferenceLogDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'system' | 'messages' | 'response'>('overview');

  useEffect(() => {
    loadLog();
  }, [inferenceLogId]);

  const loadLog = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminInferenceLogDetail(inferenceLogId);
      setLog(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inference log');
    } finally {
      setLoading(false);
    }
  };

  // Parse messages JSON safely
  let messages: Array<{ role: string; content: string }> = [];
  if (log?.messages_json) {
    try {
      messages = JSON.parse(log.messages_json);
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 border border-dark-600 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-600">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-dark-50">🔍 Inference Inspector</h2>
            {log && (
              <span className={`w-2 h-2 rounded-full ${log.success ? 'bg-green-500' : 'bg-red-500'}`} />
            )}
          </div>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-dark-50 p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="spinner w-8 h-8" />
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center text-red-400">
              {error}
            </div>
          ) : log ? (
            <>
              {/* Tabs */}
              <div className="flex border-b border-dark-600">
                {(['overview', 'system', 'messages', 'response'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-sm font-medium capitalize ${
                      activeTab === tab
                        ? 'text-primary-400 border-b-2 border-primary-400'
                        : 'text-dark-400 hover:text-dark-200'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {activeTab === 'overview' && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <InfoCard label="Request Type" value={log.request_type} />
                    <InfoCard label="Agent" value={log.agent_name || '-'} />
                    <InfoCard label="Provider" value={log.provider} />
                    <InfoCard label="Model" value={log.model} />
                    <InfoCard label="Prompt Tokens" value={log.prompt_tokens?.toLocaleString() || '-'} />
                    <InfoCard label="Completion Tokens" value={log.completion_tokens?.toLocaleString() || '-'} />
                    <InfoCard label="Latency" value={log.latency_ms ? `${log.latency_ms}ms` : '-'} />
                    <InfoCard label="Temperature" value={log.temperature?.toString() || 'default'} />
                    <InfoCard label="Started" value={new Date(log.started_at).toLocaleString()} />
                    <InfoCard label="Completed" value={log.completed_at ? new Date(log.completed_at).toLocaleString() : '-'} />
                    {!log.success && log.error_message && (
                      <div className="col-span-4 bg-red-900/30 border border-red-700 rounded p-3">
                        <div className="text-red-400 text-sm font-medium mb-1">Error</div>
                        <div className="text-red-200 text-sm">{log.error_message}</div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'system' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-dark-400">System Prompt</h3>
                      {log.system_prompt && (
                        <button
                          onClick={() => copyToClipboard(log.system_prompt || '')}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-dark-400 hover:text-primary-400 hover:bg-dark-700 rounded transition-colors"
                          title="Copy to clipboard"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Copy
                        </button>
                      )}
                    </div>
                    {log.system_prompt ? (
                      <pre className="bg-dark-700 rounded p-4 text-dark-200 text-sm whitespace-pre-wrap overflow-x-auto">
                        {log.system_prompt}
                      </pre>
                    ) : (
                      <p className="text-dark-500">No system prompt</p>
                    )}
                  </div>
                )}

                {activeTab === 'messages' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-dark-400">
                        Input Messages ({messages.length})
                      </h3>
                      {messages.length > 0 && (
                        <button
                          onClick={() => copyToClipboard(JSON.stringify(messages, null, 2))}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-dark-400 hover:text-primary-400 hover:bg-dark-700 rounded transition-colors"
                          title="Copy to clipboard"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Copy
                        </button>
                      )}
                    </div>
                    {messages.map((msg, i) => (
                      <div
                        key={i}
                        className={`rounded p-3 ${
                          msg.role === 'user'
                            ? 'bg-primary-900/30 border border-primary-800'
                            : msg.role === 'assistant'
                            ? 'bg-dark-700'
                            : 'bg-dark-600'
                        }`}
                      >
                        <div className="text-xs font-medium text-dark-400 uppercase mb-1">
                          {msg.role}
                        </div>
                        <pre className="text-dark-200 text-sm whitespace-pre-wrap">
                          {msg.content}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'response' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-dark-400">LLM Response</h3>
                      {log.response_content && (
                        <button
                          onClick={() => copyToClipboard(log.response_content || '')}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-dark-400 hover:text-primary-400 hover:bg-dark-700 rounded transition-colors"
                          title="Copy to clipboard"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Copy
                        </button>
                      )}
                    </div>
                    {log.response_content ? (
                      <pre className="bg-dark-700 rounded p-4 text-dark-200 text-sm whitespace-pre-wrap overflow-x-auto">
                        {log.response_content}
                      </pre>
                    ) : (
                      <p className="text-dark-500">No response content</p>
                    )}
                    {log.tool_calls_json && (
                      <div className="mt-4">
                        <h3 className="text-sm font-medium text-dark-400 mb-2">Tool Calls</h3>
                        <pre className="bg-dark-700 rounded p-4 text-dark-200 text-sm whitespace-pre-wrap overflow-x-auto">
                          {JSON.stringify(JSON.parse(log.tool_calls_json), null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-dark-700 rounded p-3">
      <div className="text-dark-400 text-xs mb-1">{label}</div>
      <div className="text-dark-50 font-medium text-sm truncate">{value}</div>
    </div>
  );
}
